// Vulnerability Details Component
// Displays comprehensive information about vulnerabilities in a trust relationship

import { marked } from 'marked';
import { COLOR_TO_TYPE } from '../_ui/_utils/useValidationUtils';
import { delay } from '../_ui/_utils/useUiUtils';

const IMPACT_MAPPINGS = {
    critical: {
        label: 'Critical',
        icon: 'fa-triangle-exclamation',
        color: 'red',
        class: 'text--red',
        iconType: COLOR_TO_TYPE['red']
    },
    high: {
        label: 'High',
        icon: 'fa-chevrons-up',
        color: 'orange',
        class: 'text--orange',
        iconType: COLOR_TO_TYPE['orange']
    },
    medium: {
        label: 'Medium',
        icon: 'fa-bug',
        color: 'yellow',
        class: 'text--yellow',
        iconType: COLOR_TO_TYPE['yellow']
    },
    low: {
        label: 'Low',
        icon: 'fa-chevrons-down',
        color: 'green',
        class: 'text--green',
        iconType: COLOR_TO_TYPE['green']
    },
    info: {
        label: 'Info',
        icon: 'fa-circle-info',
        color: 'blue',
        class: 'text--blue',
        iconType: COLOR_TO_TYPE['info']
    },
    investigate: {
        label: 'Investigate',
        icon: 'fa-binoculars',
        color: 'light-slate',
        class: 'text--light-slate',
        iconType: COLOR_TO_TYPE['light-slate']
    },
    unknown: {
        label: 'Unknown',
        icon: 'fa-question-circle',
        color: 'light-slate',
        class: 'text--light-slate',
        iconType: COLOR_TO_TYPE['light-slate']
    }
}

const STATUS_MAPPINGS = {
    unfixed: {
        label: 'Unfixed',
        color: 'red'
    },
    fixed: {
        label: 'Fixed',
        color: 'green'
    },
    false_positive: {
        label: 'False Positive',
        color: 'blue'
    },
    acceptable_risk: {
        label: 'Acceptable Risk',
        color: 'yellow'
    },
    unknown: {
        label: 'Unknown',
        color: 'light-slate'
    }
};

const ApckVulnDetailsHeader = {
    props: {
        domain: {
            type: String,
            required: true
        },
        thirdparty: {
            type: Object,
            required: true
        },
        vulnerability: {
            type: Object,
            required: true
        },
        impact: {
            type: Object,
            required: true
        },
        status: {
            type: Object,
            required: true
        },
    },
    computed: {
        combinedUrls() {
            const urls = [];

            if (this.vulnerability?.target_url) {
                urls.push({
                    label: 'Target URL:',
                    url: this.vulnerability.target_url
                });
            }

            if (this.vulnerability?.parent_url) {
                urls.push({
                    label: 'Parent URL:',
                    url: this.vulnerability.parent_url
                });
            }

            return urls;
        },
    },
    template: `
        <ui-layout direction="column">
            <ui-layout gap="2" align="center" wrap>
                <ui-icon v-if="impact.icon" :icon="impact.icon" :type="impact.iconType" />

                <ui-heading tag="span" :class="impact.class">
                    {{ vulnerability.name }}
                </ui-heading>

                <ui-badge :label="status.label" :color="status.color" fill />
            </ui-layout>

            <ui-layout wrap>
                <ui-layout align="center" justify="start" gap="2">
                    <ui-heading tag="h3" size="md">
                        Domain:
                    </ui-heading>
    
                    <ui-heading tag="span">
                        {{ domain }}
                    </ui-heading>
                </ui-layout>
    
    
                <ui-layout direction="row" align="center" justify="start" gap="2">
                    <ui-heading tag="h3" size="md">
                        Third-party:
                    </ui-heading>
    
                    <ui-layout
                        align="center"
                        gap="2"
                        wrap
                    >
                        <ui-heading tag="span">
                            {{ thirdparty?.hostname }}
                        </ui-heading>
    
                        <ui-heading
                            v-if="thirdparty?.service_name"
                            tag="span"
                        >
                            {{ thirdparty?.service_name }}
                        </ui-heading>
                    </ui-layout>
                </ui-layout>
            </ui-layout>
            
            <template v-if="combinedUrls?.length > 0">
                <ui-layout
                    v-for="item in combinedUrls"
                    :key="item.url"
                    align="center"
                    gap="2"
                    wrap
                >
                    <ui-heading tag="h3" size="md">
                        {{ item.label }}
                    </ui-heading>

                    <ui-button
                        type="link"
                        :label="item.url"
                        :url="item.url"
                        iconRight="fa-external-link"
                        size="sm"
                        external
                        class="text--ellipsis"
                    />
                </ui-layout>
            </template>
        </ui-layout>
    `
}

const ApckVulnDetailsInfo = {
    props: {
        countryInfo: {
            type: Object,
            required: false
        },
        vulnerability: {
            type: Object,
            required: true
        },
        impact: {
            type: Object,
            required: true
        },
        baseImpact: {
            type: Object,
            required: false
        },
        status: {
            type: Object,
            required: true
        },
        trustLevel: {
            type: Object,
            required: true
        }
    },
    computed: {
        formattedFirstSeen() {
            return this.formatDate(this.vulnerability.first_seen_at);
        },
        formattedLastSeen() {
            return this.formatDate(this.vulnerability.last_seen_at);
        },
    },
    methods: {
        formatDate(dateString) {
            if (!dateString) return 'N/A';
            try {
                const date = new Date(dateString);
                return date.toLocaleString('en-GB', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                });
            } catch (e) {
                return dateString;
            }
        },
    },
    template: `
        <ui-layout wrap justify="stretch">
            <ui-panel-base direction="column" align="start" justify="start" gap="1">
                <ui-heading tag="span" color="secondary" weight="normal">
                    Impact:
                </ui-heading>

                <ui-badge :label="impact.label" :color="impact.color" fill />
            </ui-panel-base>

            <ui-panel-base v-if="baseImpact" direction="column" align="start" justify="start" gap="1">
                <ui-heading tag="span" color="secondary" weight="normal">
                    Base Impact:
                </ui-heading>

                <ui-badge :label="baseImpact.label" :color="baseImpact.color" fill />
            </ui-panel-base>

            <ui-panel-base direction="column" align="start" justify="start" gap="1">
                <ui-heading tag="span" color="secondary" weight="normal">
                    Trust Level:
                </ui-heading>

                <ui-badge :label="trustLevel.label" :color="trustLevel.color" fill />
            </ui-panel-base>

            <ui-panel-base direction="column" align="start" justify="start" gap="1">
                <ui-heading tag="span" color="secondary" weight="normal">
                    Status:
                </ui-heading>

                <ui-badge :label="status.label" :color="status.color" fill />
            </ui-panel-base>

            <template v-if="countryInfo?.length > 0">
                <ui-panel-base direction="column" align="start" justify="start" gap="1">
                    <ui-heading tag="span" color="secondary" weight="normal">
                        Country:
                    </ui-heading>

                    <ui-layout align="center" gap="2" wrap>
                        <ui-icon
                            icon="fa-map-marker-alt"
                            size="sm"
                            type="secondary"
                        />

                        <p>{{ countryInfo?.name }} ({{ countryInfo?.iso_code }})</p>
                    </ui-layout>
                </ui-panel-base>
            </template>

            <ui-panel-base direction="column" align="start" justify="start" gap="1">
                <ui-heading tag="span" color="secondary" weight="normal">
                    First seen:
                </ui-heading>

                <ui-heading tag="span">
                    {{ formattedFirstSeen }}
                </ui-heading>
            </ui-panel-base>

            <ui-panel-base direction="column" align="start" justify="start" gap="1">
                <ui-heading tag="span" color="secondary" weight="normal">
                    Last seen:
                </ui-heading>

                <ui-heading tag="span">
                    {{ formattedLastSeen }}
                </ui-heading>
            </ui-panel-base>
        </ui-layout>
    `
};

const ApckVulnWorkflowForm = {
    props: {
        vulnerability: {
            type: Object,
            required: true
        }
    },
    data() {
        const status = this.vulnerability?.status?.toLowerCase() || 'unfixed';
        const impact = this.vulnerability?.impact || '0.0-investigate';

        return {
            selectedStatus: status,
            selectedImpact: impact,
            note: '',
            submitting: false,
            error: null,
            success: false,
            successMessage: '',
            initialStatus: status,
            initialImpact: impact,
            statusOptions: [
                {
                    label: 'Unfixed',
                    value: 'unfixed'
                },
                {
                    label: 'Fixed',
                    value: 'fixed'
                },
                {
                    label: 'False Positive',
                    value: 'false_positive'
                },
                {
                    label: 'Acceptable Risk',
                    value: 'acceptable_risk'
                }
            ],
            impactOptions: [
                {
                    label: 'Critical',
                    value: '5.0-critical'
                },
                {
                    label: 'High',
                    value: '4.0-high'
                },
                {
                    label: 'Medium',
                    value: '3.0-medium'
                },
                {
                    label: 'Low',
                    value: '2.0-low'
                },
                {
                    label: 'Info',
                    value: '1.0-info'
                },
                {
                    label: 'Investigate',
                    value: '0.0-investigate'
                }
            ]
        };
    },
    computed: {
        currentStatusLabel() {
            const status = this.vulnerability?.status?.toLowerCase() || 'unfixed';
            const mapping = STATUS_MAPPINGS[status] || STATUS_MAPPINGS.unknown;
            return mapping.label;
        },
        currentImpactLabel() {
            const impact = this.vulnerability?.impact?.toLowerCase() || 'unknown';

            for (const [key, value] of Object.entries(IMPACT_MAPPINGS)) {
                if (impact.includes(key)) {
                    return value.label;
                }
            }

            return IMPACT_MAPPINGS.unknown.label;
        },
        hasStatusChanged() {
            if (this.selectedStatus === null || this.initialStatus === null) return false;
            return this.selectedStatus !== this.initialStatus;
        },
        hasImpactChanged() {
            if (this.selectedImpact === null || this.initialImpact === null) return false;
            return this.selectedImpact !== this.initialImpact;
        },
        canSubmit() {
            const hasChanges = this.hasStatusChanged || this.hasImpactChanged;
            return !this.submitting && hasChanges;
        }
    },

    watch: {
        'vulnerability.status': {
            handler(newStatus) {
                const status = newStatus?.toLowerCase() || 'unfixed';
                this.initialStatus = status;
                this.selectedStatus = status;
            }
        },
        'vulnerability.impact': {
            handler(newImpact) {
                const impact = newImpact || '0.0-investigate';
                this.initialImpact = impact;
                this.selectedImpact = impact;
            }
        }
    },
    methods: {
        async submitChanges() {
            if (!this.selectedStatus) {
                this.error = 'Please select a status';
                return;
            }

            if (!this.selectedImpact) {
                this.error = 'Please select an impact level';
                return;
            }

            // Check if there are any actual changes to submit
            const statusChanged = this.selectedStatus !== this.initialStatus;
            const impactChanged = this.selectedImpact !== this.initialImpact;
            const hasNote = this.note && this.note.trim().length > 0;

            // Need at least one of: status change, impact change, or a note
            if (!statusChanged && !impactChanged && !hasNote) {
                this.error = 'Please make a change or add a note before updating';
                return;
            }

            // Build automatic note for changes
            const changeNotes = [];

            if (statusChanged) {
                const oldStatusLabel = this.statusOptions.find(opt => opt.value === this.initialStatus)?.label || this.initialStatus;
                const newStatusLabel = this.statusOptions.find(opt => opt.value === this.selectedStatus)?.label || this.selectedStatus;
                changeNotes.push(`Status changed from "${oldStatusLabel}" to "${newStatusLabel}"`);
            }

            if (impactChanged) {
                const oldImpactLabel = this.impactOptions.find(opt => opt.value === this.initialImpact)?.label || this.initialImpact;
                const newImpactLabel = this.impactOptions.find(opt => opt.value === this.selectedImpact)?.label || this.selectedImpact;
                changeNotes.push(`Impact changed from "${oldImpactLabel}" to "${newImpactLabel}"`);
            }

            // Combine automatic notes with user note
            let finalNote = changeNotes.join('\n\n');
            if (hasNote) {
                finalNote = finalNote ? `${finalNote}\n\n${this.note}` : this.note;
            }

            this.submitting = true;
            this.error = null;
            this.success = false;

            try {
                // Build the request payload with only changed fields
                const payload = {};

                if (statusChanged) {
                    payload.new_status = this.selectedStatus;
                }

                if (impactChanged) {
                    payload.new_impact = this.selectedImpact;
                }

                // Always include the note if there are any changes
                if (finalNote) {
                    payload.note = finalNote;
                }

                const response = await fetch(`/trust/vulnerabilities/${this.vulnerability._id}/update`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(payload)
                });

                const data = await response.json();

                if (data.success) {
                    this.success = true;
                    this.successMessage = 'Vulnerability updated successfully';
                    this.note = '';
                    this.$emit('vulnerability-changed', data.vulnerability);
                } else {
                    this.error = data.message || 'Failed to update vulnerability';
                }
            } catch (err) {
                this.error = 'Error updating vulnerability: ' + err.message;
                console.error('Error updating vulnerability:', err);
            } finally {
                this.submitting = false;
                setTimeout(() => {
                    this.success = false;
                }, 3000);
            }
        }
    },
    template: `
        <ui-layout direction="column" gap="2" innerColor="secondary">
            
            <ui-alert :open="!!error" state="destructive">
                {{ error }}
            </ui-alert>
            
            <ui-alert :open="success" state="success">
                {{ successMessage }}
            </ui-alert>

            <ui-layout direction="column" gap="3">
                <ui-input-select
                    :modelValue="selectedStatus"
                    @update:modelValue="val => selectedStatus = val"
                    label="Status"
                    :options="statusOptions"
                    :disabled="submitting"
                    align="left"
                    grow
                />

                <ui-input-select
                    :modelValue="selectedImpact"
                    @update:modelValue="val => selectedImpact = val"
                    label="Impact Level"
                    :options="impactOptions"
                    :disabled="submitting"
                    align="left"
                    grow
                />

                <ui-input-textarea
                    :modelValue="note"
                    @update:modelValue="val => note = val"
                    label="Note"
                    placeholder="Add a note about this change..."
                    :rows="4"
                    :disabled="submitting"
                    :show-optional-flag="true"
                />

                <ui-button
                    label="Update Vulnerability"
                    @click="submitChanges"
                    :loading="submitting"
                    size="md"
                    color="primary"
                />
            </ui-layout>
        </ui-layout>
    `
};

const ApckVulnWorkflowNotes = {
    props: {
        notes: {
            type: Array,
            default: () => []
        }
    },
    computed: {
        sortedNotes() {
            if (!this.notes || this.notes.length === 0) return [];

            return [...this.notes].sort((a, b) => {
                const dateA = new Date(a.created);
                const dateB = new Date(b.created);
                return dateB - dateA; // Most recent first
            });
        }
    },
    methods: {
        renderMarkdown(text) {
            if (!text) return '';
            return marked.parse(text);
        },
        formatDate(dateString) {
            if (!dateString) return '';

            const date = new Date(dateString);
            const fullDate = date.toLocaleDateString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });

            const now = new Date();
            const diffMs = now - date;
            const diffMins = Math.floor(diffMs / 60000);
            const diffHours = Math.floor(diffMs / 3600000);
            const diffDays = Math.floor(diffMs / 86400000);

            let relative = '';
            if (diffMins < 1) relative = 'Just now';
            else if (diffMins < 60) relative = `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
            else if (diffHours < 24) relative = `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
            else if (diffDays < 7) relative = `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
            else relative = fullDate;

            return relative === fullDate ? fullDate : `${relative} (${fullDate})`;
        },
        getStatusChangeLabel(from, to) {
            if (!from || !to) return '';

            const fromLabel = STATUS_MAPPINGS[from]?.label || from;
            const toLabel = STATUS_MAPPINGS[to]?.label || to;

            return `Status changed from ${fromLabel} to ${toLabel}`;
        }
    },
    template: `
        <ui-layout direction="column" innerColor="secondary">
            
            <ui-layout v-if="sortedNotes.length === 0" direction="column" align="center" gap="2">
                <ui-heading tag="p" color="secondary" size="sm">
                    No notes yet
                </ui-heading>
            </ui-layout>

            <ui-layout v-else direction="column" gap="3">
                <ui-panel-base 
                    v-for="(note, index) in sortedNotes" 
                    :key="index"
                    direction="column" 
                    gap="2"
                    innerColor="primary"
                >
                    <ui-layout direction="row" justify="space-between" align="center">
                        <ui-heading tag="span" weight="semibold">
                            {{ note.user?.name || 'Unknown User' }}
                        </ui-heading>
                        <ui-layout direction="column" align="end" gap="1">
                            <ui-heading tag="span" color="secondary">
                                {{ formatDate(note.created) }}
                            </ui-heading>
                        </ui-layout>
                    </ui-layout>

                    <ui-layout v-if="note.status_change_from && note.status_change_to" direction="column" gap="1">
                        <ui-heading tag="span" color="secondary" weight="normal">
                            {{ getStatusChangeLabel(note.status_change_from, note.status_change_to) }}
                        </ui-heading>
                    </ui-layout>

                    <ui-heading tag="div" color="primary" v-html="renderMarkdown(note.comment)" />
                </ui-panel-base>
            </ui-layout>
        </ui-layout>
    `
};

const ApckTrustDataItem = {
    props: {
        item: {
            type: Object,
            required: true
        }
    },
    methods: {
        renderMarkdown(text) {
            if (!text) return '';
            return marked.parse(text);
        }
    },
    template: `
        <ui-layout direction="column" gap="2" justify="stretch">
            <ui-layout direction="row" gap="2" align="center" wrap>
                <ui-heading tag="span" color="secondary" weight="normal" size="sm">
                    Target URL:
                </ui-heading>
                <ui-button
                    v-if="item.target_url"
                    type="link"
                    :label="item.target_url"
                    :url="item.target_url"
                    iconRight="fa-external-link"
                    size="sm"
                    external
                />
                <ui-heading v-else tag="span" size="sm">N/A</ui-heading>
            </ui-layout>
            
            <ui-layout v-if="item.source_url" direction="row" gap="2" align="center" wrap>
                <ui-heading tag="span" color="secondary" weight="normal" size="sm">
                    Source URL:
                </ui-heading>
                <ui-button
                    type="link"
                    :label="item.source_url"
                    :url="item.source_url"
                    iconRight="fa-external-link"
                    size="sm"
                    external
                />
            </ui-layout>
            
            <ui-layout v-if="item.high_level_bond_description" direction="column" gap="1">
                <ui-heading tag="span" color="secondary" weight="normal" size="sm">
                    Description:
                </ui-heading>
                <p class="trust-data-description">{{ item.high_level_bond_description }}</p>
            </ui-layout>
            
            <ui-panel-accordion
                v-if="item.detailed_bond_description"
                title="Detailed Description"
                titleSize="sm"
                :defaultOpen="false"
            >
                <div v-html="renderMarkdown(item.detailed_bond_description)"></div>
            </ui-panel-accordion>
            
            <ui-panel-accordion
                v-if="item.source_reference_snips"
                title="Source Code Snippet"
                titleSize="sm"
                :defaultOpen="false"
            >
                <pre class="trust-data-code-snippet">{{ item.source_reference_snips }}</pre>
            </ui-panel-accordion>
            
            <ui-panel-accordion
                v-if="item.target_signature"
                title="Technical Signature"
                titleSize="sm"
                :defaultOpen="false"
            >
                <pre>
                    <code class="trust-data-signature">{{ item.target_signature }}</code>
                </pre>
            </ui-panel-accordion>
            
            <ui-layout v-if="item.additional_sources && item.additional_sources.length > 0" direction="column" gap="1">
                <ui-heading tag="span" color="secondary" weight="normal" size="sm">
                    Additional Sources:
                </ui-heading>
                <ui-layout tag="ul" direction="column" gap="0" class="trust-data-sources-list">
                    <li v-for="(source, idx) in item.additional_sources" :key="idx">
                        {{ source }}
                    </li>
                </ui-layout>
            </ui-layout>
        </ui-layout>
    `
};

const ApckVulnTrustRelationship = {
    components: {
        'apck-trust-data-item': ApckTrustDataItem
    },
    props: {
        relationship: {
            type: Object,
            required: false
        }
    },
    computed: {
        hasRelationshipData() {
            return this.relationship && Object.keys(this.relationship).length > 0;
        },
        trustLevelClass() {
            const level = this.relationship?.highest_trust_level?.level || 0;
            if (level >= 4) return 'text--red';
            if (level >= 3) return 'text--orange';
            if (level >= 2) return 'text--yellow';
            return 'text--green';
        },
        renderedTrustDescription() {
            if (!this.relationship?.highest_trust_level?.description) return '';
            return this.renderMarkdown(this.relationship.highest_trust_level.description);
        },
        highestVulnImpactLabel() {
            const impact = this.relationship?.highest_vulnerability_impact;
            if (!impact) return 'N/A';

            // Map impact values like "4.0-high" to readable labels
            const impactMap = {
                '5.0-critical': 'Critical',
                '4.0-high': 'High',
                '3.0-medium': 'Medium',
                '2.0-low': 'Low',
                '1.0-info': 'Info',
                '0.0-investigate': 'Investigate'
            };

            return impactMap[impact] || impact;
        },
        highestVulnImpactClass() {
            const impact = this.relationship?.highest_vulnerability_impact;
            if (!impact) return '';

            if (impact.includes('critical')) return 'text--red';
            if (impact.includes('high')) return 'text--orange';
            if (impact.includes('medium')) return 'text--yellow';
            if (impact.includes('low')) return 'text--green';
            if (impact.includes('info')) return 'text--blue';
            return '';
        }
    },
    methods: {
        renderMarkdown(text) {
            if (!text) return '';
            return marked.parse(text);
        },
        getTrustImpactClass(impact) {
            if (impact >= 4) return 'text--red';
            if (impact >= 3) return 'text--orange';
            if (impact >= 2) return 'text--yellow';
            return 'text--green';
        },
        getTrustLevelClass(level) {
            const colorMap = {
                5: 'text--red',      // Highest
                4: 'text--orange',   // High
                3: 'text--yellow',   // Medium
                2: 'text--green',    // Low
                1: 'text--blue',     // Informational
                0: 'text--light-slate' // Investigate
            };
            return colorMap[level] || 'text--light-slate';
        },
        getTrustLevelBadge(level) {
            const trustLevelMap = {
                5: { label: 'Highest', color: 'red' },
                4: { label: 'High', color: 'orange' },
                3: { label: 'Medium', color: 'yellow' },
                2: { label: 'Low', color: 'green' },
                1: { label: 'Informational', color: 'blue' },
                0: { label: 'Investigate', color: 'light-slate' }
            };
            return trustLevelMap[level] || { label: 'Unknown', color: 'light-slate' };
        },
        parseTrustData(data) {
            // Parse the trust data structure
            // Expected format: { "2": { "description": [...items] }, "3": { "description": [...items] } }
            if (!data || typeof data !== 'object') return [];

            const parsed = [];

            // Iterate through impact levels
            Object.keys(data).forEach(impactLevel => {
                const impactData = data[impactLevel];

                if (!impactData || typeof impactData !== 'object') return;

                // Each impact level has descriptions as keys with arrays of items
                Object.keys(impactData).forEach(description => {
                    const items = impactData[description];

                    if (Array.isArray(items)) {
                        parsed.push({
                            impactLevel: parseInt(impactLevel),
                            description: description,
                            items: items
                        });
                    }
                });
            });

            // Sort by impact level descending
            return parsed.sort((a, b) => b.impactLevel - a.impactLevel);
        }
    },
    template: `
        <ui-layout v-if="!hasRelationshipData" direction="column" align="center" gap="2" justify="stretch">
            <ui-heading tag="p" color="secondary" size="sm">
                No trust relationship data available
            </ui-heading>
        </ui-layout>
        
        <ui-layout v-else direction="column" gap="2" justify="stretch">

            <ui-layout 
                v-if="relationship?.discovered_trusts?.length"
                v-for="(trust, index) in relationship.discovered_trusts" 
                :key="index"
                direction="column" 
                gap="2"
                justify="stretch"
            >                            
                    <template v-if="trust.data && Object.keys(trust.data).length > 0">
                        <ui-layout 
                            v-for="(groupData, impactLevel) in parseTrustData(trust.data)" 
                            :key="impactLevel"
                            direction="column" 
                            gap="2"
                            justify="stretch"
                        >
                            <ui-layout direction="row" align="center" gap="2">
                                <ui-heading tag="h6" :class="getTrustLevelClass(groupData.impactLevel)">
                                    {{ groupData.description }}
                                </ui-heading>
                                <ui-badge 
                                    :label="getTrustLevelBadge(groupData.impactLevel).label"
                                    :color="getTrustLevelBadge(groupData.impactLevel).color"
                                    size="xs"
                                />
                            </ui-layout>
                            
                            <ui-layout direction="column" gap="2" justify="stretch">
                                <apck-trust-data-item
                                    v-for="(item, itemIdx) in groupData.items"
                                    :key="itemIdx"
                                    :item="item"
                                />
                            </ui-layout>
                        </ui-layout>
                    </template>
            </ui-layout>

        </ui-layout>
    `
};

const ApckVulnDetailsAccordions = {
    components: {
        'apck-vuln-workflow-form': ApckVulnWorkflowForm,
        'apck-vuln-workflow-notes': ApckVulnWorkflowNotes,
        'apck-vuln-trust-relationship': ApckVulnTrustRelationship
    },
    props: {
        vulnerability: {
            type: Object,
            required: true
        },
        relationship: {
            type: Object,
            required: false
        }
    },
    data() {
        return {
            activeTabId: 'description'
        }
    },
    computed: {
        tabs() {
            const baseTabs = [
                {
                    title: 'Description',
                    id: 'description'
                },
                {
                    title: 'Details',
                    id: 'details'
                }
            ];

            // Only add Trust Relationship tab if there are discovered trusts
            if (this.relationship?.discovered_trusts?.length > 0) {
                baseTabs.push({
                    title: 'Trust Relationship',
                    id: 'trust-relationship'
                });
            }

            baseTabs.push(
                {
                    title: 'Workflow',
                    id: 'workflow'
                },
                {
                    title: 'Notes History',
                    id: 'notes'
                }
            );

            return baseTabs;
        },
        renderedSynopsis() {
            if (!this.vulnerability.synopsis) return '';
            return marked.parse(this.vulnerability.synopsis);
        },
        renderedBackground() {
            if (!this.vulnerability.background) return '';
            return marked.parse(this.vulnerability.background);
        },
        renderedTechnicalNarrative() {
            if (!this.vulnerability.technical_narrative) return '';
            return marked.parse(this.vulnerability.technical_narrative);
        },
        renderedSolution() {
            if (!this.vulnerability.solution) return '';
            return marked.parse(this.vulnerability.solution);
        },
        parsedReferences() {
            if (!this.vulnerability.references || this.vulnerability.references.length === 0) return [];

            const references = [];
            const urlRegex = /^https?:\/\/.+/i;
            const markdownLinkRegex = /^\[([^\]]+)\]\(([^)]+)\)$/;

            this.vulnerability.references.forEach(function(ref) {
                if (!ref || typeof ref !== 'string') {
                    console.warn('Invalid reference (not a string):', ref);
                    return;
                }

                const trimmedRef = ref.trim();

                // Check if it's a markdown link [label](url)
                const markdownMatch = trimmedRef.match(markdownLinkRegex);
                if (markdownMatch) {
                    const label = markdownMatch[1].trim();
                    const url = markdownMatch[2].trim();

                    // Validate URL
                    if (urlRegex.test(url)) {
                        references.push({
                            label: label,
                            url: url
                        });
                    } else {
                        console.warn('Invalid URL in markdown reference:', ref);
                    }
                    return;
                }

                // Check if it's a plain URL
                if (urlRegex.test(trimmedRef)) {
                    references.push({
                        label: trimmedRef,
                        url: trimmedRef
                    });
                    return;
                }

                // Not a valid reference format
                console.warn('Reference is neither a valid URL nor markdown link format:', ref);
            });

            return references;
        },
    },
    methods: {
        switchTab(tab) {
            this.activeTabId = tab.id;
        },
        handleVulnerabilityChanged(updatedVulnerability) {
            // Update the vulnerability prop with the new data
            // This will be emitted up to the parent component
            this.$emit('vulnerability-updated', updatedVulnerability);
        }
    },
    template: `
        <ui-layout direction="column" gap="0">
            <!-- tabs header-->
            <ui-layout direction="row" gap="0" align="end">
                <ui-button
                    v-for="tab in tabs"
                    :key="tab.id"
                    :label="tab.title"
                    :type="activeTabId === tab.id ? 'primary' : 'outline'"
                    tab
                    @click.prevent="switchTab(tab)"
                />
            </ui-layout>
            <!-- tabs content-->
            <ui-panel-base
                direction="column"
                justify="start"
                align="stretch"
                gap="2"
                class="panel-box--tab-content"
            >
                <ui-layout v-if="activeTabId === 'description'" direction="column" gap="6">
                    <ui-layout v-if="vulnerability.synopsis" direction="column" gap="2">
                        <ui-heading tag="h3">Description</ui-heading>
                        <ui-layout v-html="renderedSynopsis" direction="column"/>
                    </ui-layout>
                    
                    <ui-layout v-if="parsedReferences?.length > 0" direction="column" gap="2">
                        <ui-heading tag="h3">References</ui-heading>
                        <ui-layout tag="ul" direction="column">
                            <li v-for="(ref, index) in parsedReferences" :key="index">
                                <ui-button type="link" :label="ref.label" :url="ref.url" external />
                            </li>
                        </ui-layout>
                    </ui-layout>
                    
                    <ui-layout v-if="vulnerability.solution" direction="column" gap="2">
                        <ui-heading tag="h3">Solution</ui-heading>
                        <ui-layout v-html="renderedSolution" direction="column"/>
                    </ui-layout>
                </ui-layout>

                <ui-layout v-if="activeTabId === 'details'" gap="2" direction="column">
                    <!-- Background -->
                    <ui-panel-accordion
                        v-if="vulnerability.background"
                        title="Background"
                        titleSize="base"
                    >
                        <ui-layout v-html="renderedBackground" direction="column"/>
                    </ui-panel-accordion>

                    <!-- Technical Narrative -->
                    <ui-panel-accordion
                        v-if="vulnerability.technical_narrative"
                        title="Technical Narrative"
                        titleSize="base"
                    >
                        <ui-layout v-html="renderedTechnicalNarrative" direction="column"/>
                    </ui-panel-accordion>

                    <!-- Affected Source URLs -->
                    <ui-panel-accordion
                        v-if="vulnerability?.affected_source_urls?.length > 0"
                        title="Affected Source URLs"
                        titleSize="base"
                    >
                        <ui-layout tag="ul" direction="column">
                            <li v-for="(url, index) in vulnerability.affected_source_urls" :key="index">
                                {{ url }}
                            </li>
                        </ui-layout>
                    </ui-panel-accordion>

                    <!-- HTTP Response -->
                    <ui-panel-accordion
                        v-if="vulnerability.http_response"
                        title="HTTP Response"
                        titleSize="base"
                        innerColor="primary"
                    >
                        <pre>{{ vulnerability.http_response }}</pre>
                    </ui-panel-accordion>
                </ui-layout>

                <ui-layout v-if="activeTabId === 'trust-relationship'" gap="4" direction="column">
                    <apck-vuln-trust-relationship 
                        :relationship="relationship"
                    />
                </ui-layout>

                <ui-layout v-if="activeTabId === 'workflow'" gap="4" direction="column">
                    <apck-vuln-workflow-form 
                        :vulnerability="vulnerability"
                        @vulnerability-changed="handleVulnerabilityChanged"
                    />
                </ui-layout>
                
                <ui-layout v-if="activeTabId === 'notes'" gap="4" direction="column">
                    <apck-vuln-workflow-notes 
                        :notes="vulnerability.notes || []"
                    />
                </ui-layout>
            </ui-panel-base>
        </ui-layout>
    `
}


export const ApckTrustVulnerabilityDetails = {
    components: {
        'apck-vuln-details-info': ApckVulnDetailsInfo,
        'apck-vuln-details-header': ApckVulnDetailsHeader,
        'apck-vuln-details-accordions': ApckVulnDetailsAccordions
    },
    props: {
        vulnerabilityId: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            loading: true,
            error: null,
            vulnerability: {},
            trustDomain: {},
            thirdparty: {},
            relationship: {}
        };
    },
    computed: {
        countryInfo() {
            return this.thirdparty?.country_info || {};
        },
        impact() {
            const level = this.vulnerability?.impact?.toLowerCase() || 'unknown';

            for (const [key, value] of Object.entries(IMPACT_MAPPINGS)) {
                if (level.includes(key)) {
                    return value
                }
            }
        },
        baseImpact() {
            const rawImpact = this.vulnerability?.raw_vulnerability_impact;
            if (!rawImpact) return null;

            const level = rawImpact.toLowerCase();
            for (const [key, value] of Object.entries(IMPACT_MAPPINGS)) {
                if (level.includes(key)) {
                    return value
                }
            }
            return null;
        },
        status() {
            const key = this.vulnerability?.status?.toLowerCase() || 'unknown';
            return STATUS_MAPPINGS[key];
        },
        trustLevel() {
            const level = this.vulnerability?.trust_level || 0;

            // Map trust levels to impact-style colors and labels
            const trustLevelMap = {
                5: { label: 'Highest', color: 'red' },
                4: { label: 'High', color: 'orange' },
                3: { label: 'Medium', color: 'yellow' },
                2: { label: 'Low', color: 'green' },
                1: { label: 'Informational', color: 'blue' },
                0: { label: 'Investigate', color: 'light-slate' }
            };

            return trustLevelMap[level] || { label: 'Unknown', color: 'light-slate' };
        }
    },
    methods: {
        async loadDetails() {
            this.loading = true;
            this.error = null;

            try {
                const response = await fetch(`/trust/vulnerabilities/${this.vulnerabilityId}/details`);
                const data = await response.json();

                if (data.success) {
                    this.vulnerability = data.vulnerability;
                    this.trustDomain = data.trust_domain;
                    this.thirdparty = data.thirdparty;
                    this.relationship = data.relationship || {};
                } else {
                    this.error = data.message || 'Failed to load vulnerability details';
                }
            } catch (err) {
                this.error = 'Error loading vulnerability details: ' + err.message;
                console.error('Error loading vulnerability details:', err);
            } finally {
                await delay(200)
                this.loading = false;
            }
        },
        handleVulnerabilityUpdated(updatedVulnerability) {
            // Update the vulnerability data with the new status, impact, and notes
            // Merge the updated data with existing data
            this.vulnerability = {
                ...this.vulnerability,
                ...updatedVulnerability
            };
        }
    },
    async mounted() {
        await this.loadDetails();
    },
    template: `
        <ui-layout direction="column">
            <ui-alert :open="error" :render="false" state="destructive">
                {{ error }}
            </ui-alert>
            <template v-if="!error">
                <apck-vuln-details-header
                    :vulnerability="vulnerability"
                    :domain="trustDomain?.hostname || ''"
                    :thirdparty="thirdparty"
                    :impact="impact"
                    :status="status"
                />
                
                <apck-vuln-details-info
                    :vulnerability="vulnerability"
                    :countryInfo="countryInfo"
                    :impact="impact"
                    :baseImpact="baseImpact"
                    :status="status"
                    :trustLevel="trustLevel"
                />
   
                <ui-alert 
                    v-if="vulnerability.risk_calculation_narrative" 
                    :open="true" 
                    state="info"
                    :render="false"
                >
                    <ui-layout direction="column" gap="2">
                        
                        <ui-heading tag="span" weight="semibold">
                            Combined Risk Calculation
                        </ui-heading>
                        <ui-heading tag="p" weight="normal">
                            {{ vulnerability.risk_calculation_narrative }}
                        </ui-heading>
                        
                    </ui-layout>
                </ui-alert>

                <apck-vuln-details-accordions
                    :vulnerability="vulnerability"
                    :relationship="relationship"
                    @vulnerability-updated="handleVulnerabilityUpdated"
                />
            </template>
            
            <ui-spinner :show="loading" theme="dark" />
        </ui-layout>
    `,
};
